import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  EMPTY_GUIDE_PROGRESS,
  GUIDE_CHANGE_EVENT,
  currentGuideActivity,
  currentGuideStep,
  emitGuideChange,
  guideMode,
  guideStorage,
  loadGuideProgress,
  resolveGuideSteps,
  saveGuideProgress,
  type GuideProgress,
  type GuideStepState,
} from '../lib/first-run-guide';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from './card';

// Progress kept for this page by storage key, used only where localStorage is blocked.
const pageProgress = new Map<string, GuideProgress>();

function readProgress(key: string): GuideProgress {
  const storage = guideStorage();
  return storage
    ? loadGuideProgress(storage, key)
    : (pageProgress.get(key) ?? EMPTY_GUIDE_PROGRESS);
}

export interface FirstRunGuideController {
  progress: GuideProgress;
  /** The activity noted in this browser (opened Manage document, searched…). */
  activity: ReadonlySet<string>;
  markDone: (id: string) => void;
  dismiss: () => void;
  resume: () => void;
}

/** A guide's saved progress. Every guide mounted on the same `key` stays in step. */
export function useFirstRunGuide(key: string): FirstRunGuideController {
  const [progress, setProgress] = useState(() => readProgress(key));
  const [activity, setActivity] = useState(currentGuideActivity);
  const progressRef = useRef(progress);
  progressRef.current = progress;

  useEffect(() => {
    const sync = () => {
      setProgress(readProgress(key));
      setActivity(currentGuideActivity());
    };
    sync();
    window.addEventListener(GUIDE_CHANGE_EVENT, sync);
    return () => window.removeEventListener(GUIDE_CHANGE_EVENT, sync);
  }, [key]);

  const update = useCallback(
    (patch: (p: GuideProgress) => GuideProgress) => {
      const next = patch(readProgress(key));
      if (JSON.stringify(next) === JSON.stringify(progressRef.current)) return;
      pageProgress.set(key, next);
      saveGuideProgress(guideStorage(), key, next);
      emitGuideChange();
    },
    [key],
  );
  const markDone = useCallback(
    (id: string) => update((p) => (p.done.includes(id) ? p : { ...p, done: [...p.done, id] })),
    [update],
  );
  const dismiss = useCallback(() => update((p) => ({ ...p, dismissed: true })), [update]);
  const resume = useCallback(() => update((p) => ({ ...p, dismissed: false })), [update]);
  return { progress, activity, markDone, dismiss, resume };
}

/** Test hook: forget this page's in-memory progress. */
export function resetFirstRunGuidesForTests(): void {
  pageProgress.clear();
}

export interface GuideStepDef {
  id: string;
  title: string;
  /** One or two plain sentences. */
  description: ReactNode;
  /** True when the product's own data says this step is done. */
  done?: boolean;
  /** The step's action (a link into the feature, or a button). Call `markDone` for a
   * step the product cannot read for itself. */
  action?: (markDone: () => void) => ReactNode;
  /** Sub-steps, for a step that is really a few things in a row; done when all are. */
  parts?: {
    id: string;
    label: string;
    done?: boolean;
    action?: (markDone: () => void) => ReactNode;
  }[];
}

function Tick({ done, current, n }: { done: boolean; current: boolean; n: number }) {
  return (
    <span
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
        done
          ? 'border-primary bg-primary text-primary-foreground'
          : current
            ? 'border-primary text-primary'
            : 'border-input text-muted-foreground',
      )}
      aria-hidden="true"
    >
      {done ? '✓' : n}
    </span>
  );
}

/**
 * A role's first-run guide: a dismissible checklist panel. Shows on the first visit,
 * "Hide guide" puts it away, and a small link brings it back; it goes for good once
 * every step is done. Steps tick themselves from `done` (the product's own data), from
 * activity the product noted, or from the click on their action.
 */
export function FirstRunGuide({
  guide,
  title,
  titleAside,
  intro,
  steps,
  footer,
  resumeLabel = 'Show guide',
  ariaLabel,
  className,
  compact = false,
}: {
  guide: FirstRunGuideController;
  title: string;
  /** A "?" help tip shown after the title. */
  titleAside?: ReactNode;
  intro?: ReactNode;
  steps: readonly GuideStepDef[];
  footer?: ReactNode;
  resumeLabel?: string;
  ariaLabel?: string;
  className?: string;
  /** Shorter: only the step you are on is opened out, the rest show their titles. */
  compact?: boolean;
}) {
  const state: GuideStepState[] = resolveGuideSteps(steps, guide.progress, guide.activity);
  const mode = guideMode(state, guide.progress);
  if (mode === 'none') return null;
  if (mode === 'resume') {
    return (
      <p className={cn('text-sm', className)}>
        <button
          type="button"
          className="font-medium text-primary hover:underline"
          onClick={guide.resume}
        >
          {resumeLabel}
        </button>
      </p>
    );
  }

  const current = currentGuideStep(state);
  const doneCount = state.filter((s) => s.done).length;
  return (
    <Card role="region" aria-label={ariaLabel ?? title} className={className}>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <CardTitle>{title}</CardTitle>
            {titleAside}
          </div>
          {intro ? <p className="text-sm text-muted-foreground">{intro}</p> : null}
          <p className="text-xs text-muted-foreground">
            {doneCount} of {steps.length} done
          </p>
        </div>
        <button
          type="button"
          className="shrink-0 text-xs text-muted-foreground hover:underline"
          onClick={guide.dismiss}
        >
          Hide guide
        </button>
      </CardHeader>
      <CardContent>
        <ol className="space-y-4">
          {steps.map((step, i) => {
            const s = state[i];
            const done = s?.done === true;
            const isCurrent = step.id === current;
            const open = !compact || isCurrent;
            return (
              <li
                key={step.id}
                className="flex gap-3"
                aria-current={isCurrent ? 'step' : undefined}
              >
                <Tick done={done} current={isCurrent} n={i + 1} />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      done && 'text-muted-foreground line-through',
                    )}
                  >
                    {step.title}
                    {done ? <span className="sr-only"> (done)</span> : null}
                  </p>
                  {open ? (
                    <div className="text-sm text-muted-foreground">{step.description}</div>
                  ) : null}
                  {open && step.parts ? (
                    <ul className="space-y-1.5">
                      {step.parts.map((part, j) => {
                        const partDone = s?.parts[j]?.done === true;
                        return (
                          <li key={part.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span
                              className={cn(
                                'text-sm',
                                partDone ? 'text-muted-foreground line-through' : 'text-foreground',
                              )}
                            >
                              <span aria-hidden="true">{partDone ? '✓ ' : '• '}</span>
                              {part.label}
                              {partDone ? <span className="sr-only"> (done)</span> : null}
                            </span>
                            {part.action ? part.action(() => guide.markDone(part.id)) : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                  {open && step.action ? (
                    <div className="flex flex-wrap items-center gap-2">
                      {step.action(() => guide.markDone(step.id))}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
        {footer ? <div className="mt-4 border-t pt-3 text-sm">{footer}</div> : null}
      </CardContent>
    </Card>
  );
}
